/* eslint-disable no-undef */
import React from 'react';
import videojs from 'video.js';
import PropTypes from 'prop-types';
import CONFIG from '../../core/configs/config';

const nextCheckpointAfter = (time, step) => Math.floor(time / step) * step + step;

const blockForwardSeeking = (player, getState, setState) => {
    player.on('seeking', () => {
        const seekTime = player.currentTime();
        const { lastTimeViewed, prevTime } = getState();

        const allowedTime = Math.max(lastTimeViewed || 0, prevTime || 0);

        if (seekTime <= allowedTime + 0.5) {
            setState({ isSeek: false });
            return;
        }

        setState({ isSeek: true });
        player.currentTime(allowedTime);
    });
};

class VideoPlayer extends React.Component {
    prevTimeRef = React.createRef();
    nextAutoPauseTimeRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            lastTimeViewed: props.lastTimeViewed,
            prevTime: props.lastTimeViewed,
            isSeek: false,
            isComplete: false,
            callRefresh: false,
            callRefreshBefore: false,
            isPlaying: false,
            isAutoPausePopupVisible: false
        }
    }

    handleContinueLearning = () => {
        if (!this.player || this.props.linkGoTest) {
            return;
        }
        const { timeAutoPauseStep } = this.props;
        const currentTime = Math.floor(this.player.currentTime());
        const duration = this.player.duration();
        const next = nextCheckpointAfter(currentTime, timeAutoPauseStep);
        this.nextAutoPauseTimeRef.current = next < duration ? next : null;
        this.setState({ isAutoPausePopupVisible: false, isPlaying: true }, () => {
            this.player.play();
        });
    }

    componentDidMount() {
        const { options, disableSeekBar, onTimeUpdate, timeUpdateStep, converting,
            disableForward, timeAutoPauseStep, lastTimeViewed, disableAutoPause,
            videoLength, onRefresh, onInitVideoPlayer, indexVideoPlayer, onRunningVideo, onEndVideo, hasCompletedView } = this.props;
        
        const srcVideo = options?.sources?.[0]?.src;
        if (srcVideo === null) {
            // videojs.addLanguage('en', {'No compatible source was found for this media.': '「新規お申込み」にて登録完了後にログインしてご覧いただけます。'});
        }

        this.player = videojs(this.videoNode, {
            controls: true,
            playsinline: true,
            // controlBar: {
            //     pictureInPictureToggle: false
            // },
            ...options,
            // Avoids iOS's native webkitEnterFullScreen(), which replaces our control bar (and blockForwardSeeking) with the OS's own scrub bar.
            preferFullWindow: videojs.browser.IS_IOS
        }, () => {
            // videojs.log('onPlayerReady', this);
        });

        const player = this.player;
        const self = this;

        const initAutoPause = (duration) => {
            if (self.props.linkGoTest) return;
            // Opt-out of the periodic auto-pause popup (e.g. Share Knowledge viewing).
            if (disableAutoPause) {
                self.nextAutoPauseTimeRef.current = null;
                return;
            }
            const totalLength = videoLength || duration;
            const isCompleted = totalLength > 0 && (lastTimeViewed || 0) >= totalLength - 30;
            if (isCompleted) {
                self.nextAutoPauseTimeRef.current = null;
                return;
            }
            const next = nextCheckpointAfter(lastTimeViewed || 0, timeAutoPauseStep);
            self.nextAutoPauseTimeRef.current = next < duration ? next : null;
        };

        player.on('durationchange', function () {
            const duration = player.duration();
            if (duration && duration > 0 && duration !== Infinity && !self.nextAutoPauseTimeRef.current) {
                initAutoPause(duration);
            }
        });

        if (onInitVideoPlayer) {
            onInitVideoPlayer(player, indexVideoPlayer);
        }

        // Completed content (re-study) starts from 0; otherwise resume last position.
        if (lastTimeViewed && !hasCompletedView) {
            player.currentTime(lastTimeViewed);
        }

        if (converting) {
            player.createModal('動画の処理中のでしばらくお待ちください', { uncloseable: true });
        }

        if (disableSeekBar) {
            player.controlBar.progressControl.disable();
        }

        if (disableForward) {
            // videojs.use('*', function(player) {
            //     return {
            //         // +++ Implement setSource() +++
            //         setSource: function setSource(srcObj, next) {
            //             next(null, srcObj);
            //         },
            //         // +++ Alter the setCurrentTime method +++
            //         setCurrentTime: function setCurrentTime(ct) {
            //             const { lastTimeViewed, prevTime } = self.state;
            //             const currentTime = player.currentTime();
                        
            //             if (ct < lastTimeViewed || ct <= prevTime || ct < currentTime ) {
            //                 self.setState({ isSeek: false });
            //                 return ct;
            //             }

            //             self.setState({ isSeek: true });
            //             return currentTime;
            //         }
            //     }
            // });
            blockForwardSeeking(
                player,
                () => self.state,
                (val) => self.setState(val)
            );
        }

        if (onTimeUpdate) {
            player.on('timeupdate', function() {
                const currentTime = player.currentTime();
                const duration = player.duration();

                const { lastTimeViewed: lt, prevTime: pt } = self.state;
                const allowedTime = Math.max(lt || 0, pt || 0);

                if (currentTime > allowedTime + 2.0) {  
                    player.currentTime(allowedTime);
                    self.setState({ isSeek: true });
                    return; 
                }
                // ============================================================

                const seconds = Math.floor((currentTime % 3600) % 60);
                const { lastTimeViewed, callRefreshBefore } = self.state;


                self.setState(prev => {
                    if (prev.prevTime < currentTime) {
                        return { prevTime: currentTime }
                    }
                    return prev
                });

                // Call check is the last lecture
                if (currentTime >= duration - 10) {
                    if (!callRefreshBefore) {
                        self.setState({ callRefreshBefore: true });
                        onRefresh && onRefresh();
                    }
                }
                
                // if (duration === currentTime || currentTime >= duration - 2) {
                //     self.setState({ isComplete: true });

                //     if (!callRefresh) {
                //         self.setState({ callRefresh: true });
                //         onRefresh && onRefresh();
                //     }
                // }

                const nearEnd = duration > 0 && (currentTime >= duration - 3 || duration - currentTime <= 3);

                if (nearEnd) {
                    self.setState({
                        isComplete: true,
                        callRefresh: true
                    });

                    if (!self.state.callRefresh) {
                        onRefresh && onRefresh();
                    }

                    // Update last viewed to full duration
                    const finalTime = videoLength || duration;
                    self.handleUpdateLastTimeViewed(finalTime);
                    onTimeUpdate(currentTime, true); 
                }

                const refresh = nearEnd;

                // const refresh = duration === currentTime || currentTime >= duration - 2;

                if (seconds === 1 && seconds !== self.prevTimeRef.current && (!lastTimeViewed || lastTimeViewed < currentTime)) {
                    self.prevTimeRef.current = seconds;
                    onTimeUpdate(currentTime, refresh);
                    self.handleUpdateLastTimeViewed(currentTime);
                }

                // check update time viewed
                if ((currentTime > timeUpdateStep && seconds !== self.prevTimeRef.current && seconds % timeUpdateStep === 0) 
                    || duration === currentTime) {

                    if (!lastTimeViewed || lastTimeViewed < currentTime || lastTimeViewed === currentTime || currentTime >= duration - 2) {
                        self.prevTimeRef.current = seconds;
                        const timeUpdate = (duration === currentTime || currentTime >= duration - 2)
                            ? (videoLength || currentTime) : currentTime;
                        onTimeUpdate(timeUpdate, refresh);
                        self.handleUpdateLastTimeViewed(timeUpdate);
                    }
                }

                // check auto pause
                if (Math.floor(currentTime) > lastTimeViewed && Math.floor(currentTime) > 0 && Math.floor(currentTime) % timeAutoPauseStep === 0) {
                    if (self.prevTimePausedRef.current !== Math.floor(currentTime)) {
                        self.setState({ isSeek: false });
                        
                        const timeUpdate = (duration === currentTime || currentTime >= duration - 2)
                            ? (videoLength || currentTime) : currentTime;
                        onTimeUpdate(timeUpdate, refresh);
                        self.handleUpdateLastTimeViewed(timeUpdate);

                        player.pause();
                    }
                    self.prevTimePausedRef.current = Math.floor(currentTime)
                }
            });

            player.on('pause', function() {
                self.setState({ isPlaying: false });

                const currentTime = player.currentTime();
                const duration = player.duration();

                const { lastTimeViewed: lt, prevTime: pt } = self.state;
                const allowedTime = Math.max(lt || 0, pt || 0);

                if (currentTime > allowedTime + 2) {
                    player.currentTime(allowedTime);
                    self.setState({ isSeek: true });
                    return;
                }
                // ============================================================

                const seconds = Math.floor((currentTime % 3600) % 60);
                const { callRefreshBefore } = self.state;

                // Call check is the last lecture
                if (currentTime >= duration - 10) {
                    if (!callRefreshBefore) {
                        self.setState({ callRefreshBefore: true });
                        onRefresh && onRefresh();
                    }
                }

                if ((duration === currentTime || currentTime >= duration - 2)) {
                    self.prevTimeRef.current = seconds;
                    const timeUpdate = videoLength || currentTime;
                    onTimeUpdate(timeUpdate);
                    self.handleUpdateLastTimeViewed(timeUpdate);
                }
            })
        }

        player.on('timeupdate', function() {
            if (self.props.linkGoTest || player.paused() || !self.nextAutoPauseTimeRef.current) {
                return;
            }

            const roundedCurrentTime = Math.floor(player.currentTime());
            const nextAutoPauseTime = self.nextAutoPauseTimeRef.current;

            if (roundedCurrentTime > 0 && roundedCurrentTime >= nextAutoPauseTime) {
                player.pause();
                self.setState({ isAutoPausePopupVisible: true });
            }
        });

        player.on('play', () => {
            if (onRunningVideo) {
                onRunningVideo();
            }
            this.setState({ isPlaying: true });
        });

        player.on('ended', function () {
            // self.setState({
            //     isComplete: true,
            //     callRefresh: true
            // });
            // const finalTime = videoLength || player.duration() || 0;
            // onTimeUpdate(finalTime, true);
            // self.handleUpdateLastTimeViewed(finalTime);
            // onRefresh && onRefresh();
            self.setState({ isPlaying: false });
            if (onEndVideo) {
                onEndVideo();
            }
        });
    }

    handleUpdateLastTimeViewed = (currentTime) => {
        if (this.props.disableForward) {
            this.setState(prev => {
                if (prev.lastTimeViewed < currentTime) {
                    return { lastTimeViewed: currentTime, isSeek: false };
                }
                return { ...prev, isSeek: false };
            })
        }
    }

    componentWillUnmount() {
        if (this.player) {
            // this.player.dispose();
        }
        this.prevTimeRef.current = undefined;
        this.nextAutoPauseTimeRef.current = undefined;
    }

    render() {
        const { isComplete, isPlaying, isAutoPausePopupVisible } = this.state;
        const { showCompleteText, linkGoTest, isFinalLecture } = this.props;
        const showAutoPausePopup = !linkGoTest && isAutoPausePopupVisible;
        const showGoTestPopup = linkGoTest && !isPlaying;

        return (
            <div className='video-preview' style={{ position: 'relative' }}>
                <div className='video-player-wrapper' style={{ width: this.props.width }} data-vjs-player>
                    <video style={{ margin: 'auto' }} 
                        ref={node => this.videoNode = node}
                        width={this.props.width} height={this.props.height} className="video-js" poster={this.props.options.poster || ''}
                        playsInline
                    ></video>
                    {showAutoPausePopup && (
                        <div className='video-popup-overlay video-popup-overlay-blocking'>
                            <div className='video-popup-content video-popup-content-auto-pause'>
                                <p className='video-popup-message'>
                                    動画視聴後15分ごとに自動停止されます。<br />
                                    受講を継続される場合は「このまま受講」ボタンを押してください。
                                </p>
                                <div style={{ textAlign: 'center' }}>
                                    <button
                                        type='button'
                                        className='continue-learning'
                                        onClick={this.handleContinueLearning}
                                    >
                                        このまま受講
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {showGoTestPopup && (
                        <div className='video-popup-overlay'>
                            <div className='video-popup-content video-popup-content-go-test'>
                                <a className='go-test' href={linkGoTest}>テストを受ける</a>
                            </div>
                        </div>
                    )}
                </div>
                {isComplete && !isFinalLecture && !linkGoTest &&  showCompleteText &&
                    <p className='mg-t-10 complete-video'>本コンテンツの視聴は完了しました。下の未視聴のコンテンツのなかから、次に視聴するものをお選びください。</p>}
                {linkGoTest && <p className='mg-t-10 complete-video' style={{ textAlign: 'center' }}>全コンテンツの受講が終了しました。</p>}
            </div>
        );
    }
}

VideoPlayer.propTypes = {
    options: PropTypes.object,
    disableSeekBar: PropTypes.bool,
    disableForward: PropTypes.bool,
    converting: PropTypes.bool,
    showCompleteText: PropTypes.bool,
    onTimeUpdate: PropTypes.func,
    lastTimeViewed: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    timeUpdateStep: PropTypes.number,
    timeAutoPauseStep: PropTypes.number,
    disableAutoPause: PropTypes.bool,
    videoLength: PropTypes.number,
    linkGoTest: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    onRefresh: PropTypes.func,
    isFinalLecture: PropTypes.bool,
    indexVideoPlayer: PropTypes.number,
    onInitVideoPlayer: PropTypes.func,
    hasCompletedView: PropTypes.bool
}

VideoPlayer.defaultProps = {
    timeUpdateStep: CONFIG.timeUpdateStep,
    timeAutoPauseStep: CONFIG.timeAutoPauseStep,
    disableSeekBar: false,
    disableForward: true,
    disableAutoPause: false,
    options: {},
    width: 768,
    height: 432,
    converting: false,
    showCompleteText: false,
    hasCompletedView: false
}

export default VideoPlayer;